import { ItemCodeEnum, ResultType } from "@app/constants";
import type {
  ItemDraft,
  TemplateInspectionItem,
} from "./createInspectionForm.types";

export const INSPECTION_MEDIA_TOTAL_LIMIT = 50;
export const INSPECTION_AT_MIN_DATE = new Date(2024, 0, 1, 0, 0, 0, 0);

export function isInspectionAtInValidRange(
  value: Date | null | undefined,
): boolean {
  if (!value) return false;
  const max = new Date();
  return (
    value.getTime() >= INSPECTION_AT_MIN_DATE.getTime() &&
    value.getTime() <= max.getTime()
  );
}

const NUMERIC_SMR_MAX = 999999.9;
const NUMERIC_ODO_MAX = 999999.9;
const NUMERIC_INPUT_PATTERN = /^\d*\.?\d{0,1}$/;

export function isIconResultItem(item: TemplateInspectionItem): boolean {
  const isNumeric =
    item.resultType === ResultType.NUMERIC ||
    item.resultType === ResultType.ODOMETER;
  return (
    item.resultType !== ResultType.NONE &&
    item.resultType !== ResultType.FREE_TEXT &&
    !isNumeric
  );
}

export function hasPredefinedOptions(item: TemplateInspectionItem): boolean {
  return (item.options?.length ?? 0) > 0;
}

export function sortByPosition<T extends { position?: number | null }>(
  items: T[] | null | undefined,
): T[] {
  if (!items?.length) return [];
  return [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

export function getSortedPredefinedOptions(item: TemplateInspectionItem) {
  return sortByPosition(item.options ?? []);
}

function isOdometerItem(item: TemplateInspectionItem): boolean {
  return (
    item.itemCode === ItemCodeEnum.ODOMETER ||
    item.resultType === ResultType.ODOMETER
  );
}

export function getNumericMax(item: TemplateInspectionItem): number {
  return isOdometerItem(item) ? NUMERIC_ODO_MAX : NUMERIC_SMR_MAX;
}

export function getNumericFieldLabel(
  item: TemplateInspectionItem,
  t: (key: string) => string,
): string {
  const label = isOdometerItem(item) ? t("odometer") : t("serviceMeter");
  // MUI TextField `required` adds its own asterisk — strip any from locale.
  return label.replace(/\*+$/, "");
}

export function getNumericUnitSuffix(item: TemplateInspectionItem): string {
  return isOdometerItem(item) ? "km" : "h";
}

function stripLeadingZerosFromNumeric(value: string): string {
  if (!value) return value;
  if (value.startsWith("0.")) return value;

  if (value.includes(".")) {
    const [intPart, ...decParts] = value.split(".");
    const decPart = decParts.join("").replace(/\./g, "");
    const strippedInt = intPart.replace(/^0+/, "") || "0";
    if (value.endsWith(".") && decPart === "") {
      return `${strippedInt}.`;
    }
    return `${strippedInt}.${decPart}`;
  }

  return value.replace(/^0+/, "") || "0";
}

function enforceNumericDecimalFormat(value: string): string {
  const dotIndex = value.indexOf(".");
  if (dotIndex === -1) return value;

  const intPart = value.slice(0, dotIndex);
  const decPart = value.slice(dotIndex + 1).replace(/\./g, "");
  if (value.endsWith(".") && decPart === "") {
    return `${intPart}.`;
  }
  return `${intPart}.${decPart.slice(0, 1)}`;
}

export function sanitizeNumericInputValue(
  raw: string,
  fallback: string,
): string {
  if (!NUMERIC_INPUT_PATTERN.test(raw)) {
    return fallback;
  }
  const withoutLeadingZeros = stripLeadingZerosFromNumeric(raw);
  return enforceNumericDecimalFormat(withoutLeadingZeros);
}

export function isNumericValueValid(value: string, max: number): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.endsWith(".")) {
    return false;
  }
  if (!/^\d+\.\d$/.test(trimmed) && !/^\d+$/.test(trimmed)) {
    return false;
  }
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= max;
}

export function normalizeNumericOnBlur(value: string, max: number): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  let normalized = enforceNumericDecimalFormat(
    stripLeadingZerosFromNumeric(trimmed),
  );
  if (/^\d+$/.test(normalized)) {
    normalized = `${normalized}.0`;
  } else if (normalized.endsWith(".")) {
    normalized = `${normalized}0`;
  }

  const parsed = parseFloat(normalized);
  if (Number.isFinite(parsed) && parsed > max) {
    return String(max);
  }

  return normalized;
}

export function capMediaFilesForItem(
  prev: Record<string, ItemDraft>,
  itemId: string,
  files: File[],
): File[] {
  const d = prev[itemId];
  if (!d) return files;

  const otherItemsMediaCount = Object.entries(prev).reduce(
    (sum, [id, itemDraft]) =>
      id === itemId ? sum : sum + (itemDraft.mediaFiles?.length ?? 0),
    0,
  );
  const maxForThisItem = Math.min(
    5,
    Math.max(0, INSPECTION_MEDIA_TOTAL_LIMIT - otherItemsMediaCount),
  );
  return files.slice(0, maxForThisItem);
}

export function buildMediaDraftUpdate(
  prev: Record<string, ItemDraft>,
  itemId: string,
  cappedFiles: File[],
) {
  const d = prev[itemId];
  if (!d) return prev;

  const existingFiles = d.mediaFiles.map(
    (file: File & { filePath?: string; mediaUrl?: string }) =>
      file.filePath || file.mediaUrl || "",
  );
  const newFiles = cappedFiles.map(
    (file: File & { filePath?: string; mediaUrl?: string }) =>
      file.filePath || file.mediaUrl || "",
  );
  const removedFiles = existingFiles.filter(
    (mediaUrl: string) => mediaUrl && !newFiles.includes(mediaUrl),
  );
  let removedMediaUrls = d.removedMediaUrls;
  if (removedFiles.length > 0) {
    removedMediaUrls = [...d.removedMediaUrls, ...removedFiles];
  }

  return {
    ...prev,
    [itemId]: {
      ...d,
      mediaFiles: cappedFiles,
      fileCount: cappedFiles.length,
      removedMediaUrls,
    },
  };
}
